import React, { useState } from 'react';
import { Card } from '../../../../../components/Card/Card';
import { t } from '../../../../../locales/translate';
import { PermissionButton } from '../../../../../components/Button/PermissionButton';
import { Event } from '../../../../../model/event';
import { categoryPath, tourPath } from '../../../../../routes';
import { ShopCategoryModal } from './Modals/ShopCategoryModal';
import { Modal } from '../../../../../components/Modal/Modal';
import { Icon } from '../../../../../components/Icon/Icon';
import { Spinner } from '../../../../../components/Spinner/Spinner';
import { Product } from '../../../../../model/product';
import {
  getCategoryName,
  getCategoriesByIds,
  CategoryType,
  categoryTypes,
  OrderedCategory,
} from '../../../../../model/category';
import { useRemoteData } from '../../../../../model/data';
import { useErrorHandler } from '../../../../../util/util';
import { Stack } from '../../../../../components/Stack/Stack';
import { compact } from 'lodash';
import { Link } from 'react-router-dom';

interface Props {
  product: Event | Product;
  title?: string;
  blacklist?: CategoryType[];
  loading?: boolean;
  onChange?: () => void | Promise<void>;
}

function filterCategoriesByType(categories: OrderedCategory[], type: CategoryType): OrderedCategory[] {
  return categories.filter((item) => {
    const categoryType = item.category.type;

    if (!categoryType && type === 'category') {
      return true;
    }

    return type === categoryType;
  });
}

export function ShopCategories({ product, title, blacklist, loading, onChange }: Props): JSX.Element {
  const [showEditCategoriesModal, setShowEditCategoriesModal] = useState(false);
  const [, loadingCategories, categoriesErrors] = useRemoteData(
    () => {
      const orderedCategory = product.categories as OrderedCategory[];
      const allCategoryIds = orderedCategory.flatMap((item) => [item.category.id, item.category.parentCategoryId]);
      return getCategoriesByIds(compact(allCategoryIds));
    },
    (db) => db.table('categories').all
  );
  useErrorHandler(categoriesErrors);

  const categories = product.categories as OrderedCategory[];

  const categoriesGroups = categoryTypes.map((type) => {
    return {
      id: type,
      title: t.model[type]._plural || type,
      items: filterCategoriesByType(categories, type),
    };
  });

  return (
    <Card
      title={title || t.model.event.ShopCategories}
      elevation={1}
      actions={
        <PermissionButton
          name="edit"
          model={product}
          permission="update_categories"
          onClick={() => setShowEditCategoriesModal(true)}
        />
      }
    >
      {loading || loadingCategories ? (
        <Spinner color="var(--color-tint)" text={t.actions.loading('')} />
      ) : (
        <Stack>
          {!categories.length && t.model.event.noCategoryAssociation}
          {categoriesGroups.map((group) => {
            if (!group.items.length) return null;

            return (
              <Stack key={group.id} size="xs">
                <span className="event-shop__categories-label">{group.title}</span>
                {group.items.map((item) => {
                  const categoryTitle = getCategoryName(item.category.id, {
                    withShop: true,
                    includeShopDomain: true,
                    preferInternalName: true,
                  });
                  return (
                    <div className="event-shop__category" key={item.category.id}>
                      <Link
                        to={item.category.type === 'tour' ? tourPath(item.category.id) : categoryPath(item.category)}
                      >
                        {categoryTitle} [{item.order}]
                      </Link>
                      {item.urlInShop && (
                        <a href={item.urlInShop} target="_blank" rel="noreferrer">
                          <Icon name="external-link--sm" className="event-shop__shop-link-icon" />
                        </a>
                      )}
                    </div>
                  );
                })}
              </Stack>
            );
          })}
        </Stack>
      )}
      <Modal
        open={showEditCategoriesModal}
        size="large"
        title={
          title === t.model.shop.shopsAndCategories
            ? t.model.shop.shopsAndCategoriesModal
            : t.model.event.ShopCategoriesModalTitle
        }
      >
        <ShopCategoryModal
          product={product}
          blacklist={blacklist}
          onChange={onChange}
          onClose={() => setShowEditCategoriesModal(false)}
          addCategoryTitle={
            title === t.model.shop.shopsAndCategories ? t.actions.add(t.model.shop.shopsCategories) : undefined
          }
        />
      </Modal>
    </Card>
  );
}
